// ***************************************************************************************
// The selection path, as the C drew it.
//
// The original did NOT draw a line between tiles. game/directionarrows.cpp stamps an
// ARROW SPRITE on each tile after the first, pointing in the direction you travelled to
// reach it - the eight Arrow_N .. Arrow_NW sprites in data/tiles/tiles.xml. A thin line
// reads as a debug overlay next to it; the arrows read as part of the game.
//
// The art is recreated here rather than shipped: the originals are a chunky bright-green
// arrow with a dark outline, and redrawing that into a canvas texture means one small
// texture, no extra requests, and crisp edges at any board size. One arrow is drawn
// pointing UP and the mesh is rotated for the other seven, because the sprites are
// rotations of each other.
// ***************************************************************************************

import * as THREE from 'three';

/** Sampled from the middle of the original Arrow_* sprites. */
const ARROW_GREEN = '#5cb700';
const ARROW_GREEN_LIGHT = '#7ee01a';
const ARROW_OUTLINE = '#11250a';

const TEXTURE_PIXELS = 128;

/**
 * A fat arrow pointing up, drawn into a square canvas.
 *
 * Proportions follow the original: a head about half the width and a shaft a little
 * narrower than the head's base, so it stays legible when it is sitting on top of a
 * letter.
 */
function drawArrow(): HTMLCanvasElement {
  const canvas = document.createElement('canvas');

  canvas.width = TEXTURE_PIXELS;
  canvas.height = TEXTURE_PIXELS;

  const context = canvas.getContext('2d');

  if (!context) return canvas;

  const s = TEXTURE_PIXELS;

  // Arrow outline, in a square where (0,0) is top left and the arrow points to the top.
  const headHalf = s * 0.3;
  const shaftHalf = s * 0.145;
  const tipY = s * 0.16;
  const shoulderY = s * 0.52;
  const tailY = s * 0.84;
  const cx = s / 2;

  const path = new Path2D();

  path.moveTo(cx, tipY);
  path.lineTo(cx + headHalf, shoulderY);
  path.lineTo(cx + shaftHalf, shoulderY);
  path.lineTo(cx + shaftHalf, tailY);
  path.lineTo(cx - shaftHalf, tailY);
  path.lineTo(cx - shaftHalf, shoulderY);
  path.lineTo(cx - headHalf, shoulderY);
  path.closePath();

  context.lineJoin = 'round';
  context.lineCap = 'round';

  // A pale rim under the dark outline. The arrows now sit ON the join between two tiles
  // rather than on one tile, so they have to read against a dark blue and a bright yellow
  // at the same time. A light halo does that; a single dark outline does not.
  context.strokeStyle = 'rgba(255, 255, 255, 0.9)';
  context.lineWidth = s * 0.19;
  context.stroke(path);

  // The dark outline, drawn under the fill so the green keeps its full area.
  context.strokeStyle = ARROW_OUTLINE;
  context.lineWidth = s * 0.1;
  context.stroke(path);

  const fill = context.createLinearGradient(0, tipY, 0, tailY);

  fill.addColorStop(0, ARROW_GREEN_LIGHT);
  fill.addColorStop(1, ARROW_GREEN);

  context.fillStyle = fill;
  context.fill(path);

  return canvas;
}

/** The eight directions, as the rotation needed for an arrow drawn pointing up. */
export function arrowAngle(dx: number, dy: number): number {
  // atan2 gives the angle from +x; the drawn arrow points along +y, so take a quarter
  // turn off. dy is in BOARD coordinates, where +y is up the screen.
  return Math.atan2(dy, dx) - Math.PI / 2;
}

/**
 * A pool of arrow meshes.
 *
 * Reused rather than rebuilt: a selection changes on every pointer move, and allocating
 * geometry per move churns badly on a phone.
 */
export class DirectionArrows {
  private readonly geometry: THREE.PlaneGeometry;
  private readonly material: THREE.MeshBasicMaterial;
  private readonly texture: THREE.CanvasTexture;
  private readonly meshes: THREE.Mesh[] = [];

  constructor(
    private readonly scene: THREE.Scene,
    private readonly capacity: number,
    /** Arrow size as a fraction of a tile. The originals are about 0.4 of a 72px tile. */
    scale = 0.5,
  ) {
    this.texture = new THREE.CanvasTexture(drawArrow());
    this.texture.colorSpace = THREE.SRGBColorSpace;
    this.texture.minFilter = THREE.LinearMipmapLinearFilter;
    this.texture.magFilter = THREE.LinearFilter;

    this.material = new THREE.MeshBasicMaterial({
      map: this.texture,
      transparent: true,
      depthWrite: false,
											  });

    this.geometry = new THREE.PlaneGeometry(scale, scale);

    for (let i = 0; i < capacity; i++) {
      const mesh = new THREE.Mesh(this.geometry, this.material);

      // Above the tiles, which sit at z 0 and 0.5 when selected.
      mesh.position.z = 2;
      mesh.visible = false;

      this.meshes.push(mesh);
      this.scene.add(mesh);
    }
  }

  /**
   * Place one arrow per step of the path.
   *
   * `points` are tile centres in world coordinates, in selection order. The first tile
   * gets no arrow - there is no direction into it.
   */
  update(points: readonly { x: number; y: number }[]): void {
    for (let i = 0; i < this.meshes.length; i++) {
      const mesh = this.meshes[i]!;
      const step = i + 1;

      if (step >= points.length || step > this.capacity) {
        mesh.visible = false;
        continue;
      }

      const from = points[step - 1]!;
      const to = points[step]!;

      // On the STEP, not on the tile. The C stamped the arrow on the destination tile,
      // which on this board leaves fragments of the letter poking out from behind it -
      // untidy in a way the original's artwork was not. Sitting on the midpoint keeps
      // the bold arrow and the sense of a path, and never covers a glyph.
      mesh.position.x = (from.x + to.x) / 2;
      mesh.position.y = (from.y + to.y) / 2;
      mesh.rotation.z = arrowAngle(to.x - from.x, to.y - from.y);
      mesh.visible = true;
    }
  }

  clear(): void {
    for (const mesh of this.meshes) mesh.visible = false;
  }

  dispose(): void {
    for (const mesh of this.meshes) this.scene.remove(mesh);

    this.meshes.length = 0;
    this.geometry.dispose();
    this.material.dispose();
    this.texture.dispose();
  }
}
